import { useState, useCallback, useMemo } from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import FileDownload from "@mui/icons-material/FileDownload";
import { useAppSelector } from "@app-redux/store";
import { selectedCategoryIdSelector, selectedSubcategorySelector } from "@app-redux/selectors";
import { useOpportunities, useOpportunityCategories, useUserData, useExportOpportunities } from "@/hooks/queries";
import { useNotification } from "@/hooks/useNotification";
import { OpportunitiesSkeleton } from "@components/Skeletons";
import { apiColumnsToColumnDefs } from "./columnConfig";
import { OpportunityTable } from "./OpportunityTable";
import styles from "./styles.module.css";

export const Opportunities = () => {
  const selectedCategoryId = useAppSelector(selectedCategoryIdSelector);
  const selectedSubcategory = useAppSelector(selectedSubcategorySelector);
  const { data: userData } = useUserData();
  const isAdmin = !!userData?.isAdmin;
  const { data: oppCategories = [] } = useOpportunityCategories();
  const { data: opportunities = [], isLoading, error } = useOpportunities(selectedCategoryId, selectedSubcategory);
  const exportMutation = useExportOpportunities();
  const { notifySuccess, notifyError } = useNotification();
  const [isAddingRow, setIsAddingRow] = useState(false);

  const selectedCategory = useMemo(() => {
    if (!selectedCategoryId) return null;
    return oppCategories.find((c) => c.id === selectedCategoryId) ?? null;
  }, [oppCategories, selectedCategoryId]);

  const hasSubcategories = (selectedCategory?.subcategories?.length ?? 0) > 0;
  const needsSubcategory = hasSubcategories && !selectedSubcategory;

  const columns = useMemo(
    () => selectedCategory ? apiColumnsToColumnDefs(selectedCategory.columns) : [],
    [selectedCategory]
  );

  const handleAddNew = useCallback(() => {
    setIsAddingRow(true);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAddingRow(false);
  }, []);

  const errorMessage = error instanceof Error ? error.message : error ? String(error) : "";

  if (!selectedCategoryId) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholderWrapper}>
          <p className={styles.emptyMessage}>Select a category to view opportunities</p>
        </div>
      </div>
    );
  }

  if (needsSubcategory) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholderWrapper}>
          <p className={styles.emptyMessage}>Select a subcategory to view opportunities</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {isAdmin && selectedCategoryId ? (
        <div className={styles.toolbar}>
          <Button
            variant="contained"
            size="small"
            color="inherit"
            startIcon={<FileDownload />}
            disabled={exportMutation.isPending}
            onClick={() => {
              exportMutation.mutate(
                { categoryId: selectedCategoryId, subcategoryId: selectedSubcategory },
                {
                  onSuccess: () => notifySuccess("Export completed"),
                  onError: () => notifyError("Export failed"),
                }
              );
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add New
          </Button>
        </div>
      ) : (
        <div className={styles.spacer} />
      )}
      <div className={styles.tableWrapper}>
        {isLoading && <OpportunitiesSkeleton />}
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        {!isLoading &&
          selectedCategoryId &&
          opportunities.length === 0 &&
          !isAddingRow &&
          !errorMessage && (
            <p className={styles.emptyMessage}>
              No opportunities in this category yet
            </p>
          )}
        {(opportunities.length > 0 || isAddingRow) && selectedCategoryId && (
          <OpportunityTable
            opportunities={opportunities}
            isAdmin={isAdmin}
            isAddingRow={isAddingRow}
            onCancelAdd={handleCancelAdd}
            selectedCategoryId={selectedCategoryId}
            columns={columns}
            categoryName={selectedCategory?.name ?? ""}
            selectedSubcategory={selectedSubcategory}
            subcategories={selectedCategory?.subcategories ?? []}
          />
        )}
      </div>
    </div>
  );
};
